import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import './Management.css';

const StickerExampleManagement = () => {
  const { token } = useAuth();
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageIds, setExistingImageIds] = useState([]);
  const [formData, setFormData] = useState({
    caption: '',
    order: '',
  });

  useEffect(() => {
    fetchExamples();
  }, [token]);

  const fetchExamples = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/sticker-examples?populate=*&sort=order:asc`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Kan voorbeelden niet laden');
      const data = await response.json();
      setExamples(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const normalizeOrientation = (file) =>
    new Promise((resolve) => {
      createImageBitmap(file).then((bitmap) => {
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        bitmap.close();
        canvas.toBlob(
          (blob) => resolve(new File([blob], file.name, { type: blob.type })),
          file.type || 'image/jpeg',
          0.92
        );
      });
    });

  const uploadImage = async (file) => {
    const normalized = await normalizeOrientation(file);
    const data = new FormData();
    data.append('files', normalized);
    const response = await fetch(`${API_URL}/api/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: data,
    });
    if (!response.ok) throw new Error('Afbeelding uploaden mislukt');
    const result = await response.json();
    return result[0].id;
  };

  const resetForm = () => {
    setFormData({ caption: '', order: '' });
    setImageFile(null);
    setImagePreview(null);
    setExistingImageIds([]);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editingId ? 'PUT' : 'POST';
      const endpoint = editingId
        ? `${API_URL}/api/sticker-examples/${editingId}`
        : `${API_URL}/api/sticker-examples`;

      let imageIds = [...existingImageIds];
      if (imageFile) {
        const newId = await uploadImage(imageFile);
        imageIds = [newId];
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            caption: formData.caption,
            order: parseInt(formData.order, 10) || 0,
            image: imageIds[0] || null,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Kan voorbeeld niet opslaan');
      }
      await fetchExamples();
      resetForm();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Weet je zeker dat je dit voorbeeld wilt verwijderen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/sticker-examples/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Kan voorbeeld niet verwijderen');
      await fetchExamples();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (example) => {
    const attrs = example.attributes || example;
    const img = attrs.image?.data || attrs.image || null;
    const id = img ? (Array.isArray(img) ? img[0]?.id : img.id) : null;
    const url = img
      ? `${API_URL}${(Array.isArray(img) ? img[0]?.attributes?.url || img[0]?.url : img.attributes?.url || img.url)}`
      : null;

    setFormData({
      caption: attrs.caption || '',
      order: attrs.order ?? '',
    });
    setExistingImageIds(id ? [id] : []);
    setImagePreview(url);
    setImageFile(null);
    setEditingId(example.id);
    setShowForm(true);
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Sticker Voorbeelden</h1>
        <button className="btn-primary" onClick={() => showForm ? resetForm() : setShowForm(true)}>
          {showForm ? 'Annuleren' : '+ Voorbeeld Toevoegen'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Bijschrift</label>
            <input
              type="text"
              value={formData.caption}
              onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Volgorde</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Afbeelding</label>
            {imagePreview && (
              <div style={{ marginBottom: '8px' }}>
                <img
                  src={imagePreview}
                  alt="Voorbeeldweergave"
                  style={{ maxHeight: '120px', maxWidth: '200px', borderRadius: '4px', objectFit: 'cover' }}
                />
                <button
                  type="button"
                  style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#e53e3e', background: 'none', border: 'none', cursor: 'pointer' }}
                  onClick={() => { setImageFile(null); setImagePreview(null); setExistingImageIds([]); }}
                >
                  Afbeelding verwijderen
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setImageFile(file);
                  setImagePreview(URL.createObjectURL(file));
                }
              }}
            />
          </div>
          <button type="submit" className="btn-submit">
            {editingId ? 'Voorbeeld Bijwerken' : 'Voorbeeld Aanmaken'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="loading">Voorbeelden worden geladen...</p>
      ) : examples.length === 0 ? (
        <p className="empty">Geen voorbeelden gevonden</p>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                <th>Afbeelding</th>
                <th>Bijschrift</th>
                <th>Volgorde</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {examples.map((example) => {
                const attrs = example.attributes || example;
                const img = attrs.image?.data || attrs.image || null;
                const thumbUrl = img
                  ? `${API_URL}${(Array.isArray(img) ? img[0]?.attributes?.url || img[0]?.url : img.attributes?.url || img.url)}`
                  : null;

                return (
                  <tr key={example.id}>
                    <td>
                      {thumbUrl
                        ? <img src={thumbUrl} alt={attrs.caption} style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                        : <span style={{ color: '#aaa', fontSize: '12px' }}>Geen</span>
                      }
                    </td>
                    <td>{attrs.caption}</td>
                    <td>{attrs.order}</td>
                    <td className="actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(example)}
                      >
                        Bewerken
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(example.id)}
                      >
                        Verwijderen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StickerExampleManagement;
